import { Link, useLocation } from 'react-router-dom';
import { Building2, FlaskConical, Home, Plus, UserCircle } from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { label: 'Home', path: ROUTES.DASHBOARD, icon: Home },
  { label: 'Jobs', path: ROUTES.JOBS, icon: Building2 },
  { label: 'Add', path: ROUTES.JOB_NEW, icon: Plus, isCenter: true },
  { label: 'Analyzer', path: ROUTES.ANALYZER, icon: FlaskConical },
  { label: 'Profile', path: ROUTES.PROFILE, icon: UserCircle },
];

/**
 * Mobile bottom tab bar navigation.
 *
 * Follows the standard iOS/Android bottom tab pattern with:
 * - Home, Jobs, + (quick-add elevated), Reports, Profile
 * - The centre "+" button is visually elevated as a primary action.
 * - Safe area inset padding for notched devices.
 */
export function MobileBottomNav() {
  const location = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/40 bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 safe-bottom md:hidden">
      <div className="flex h-16 items-center justify-around px-2">
        {NAV_ITEMS.map((item) => {
          const isActive =
            item.path === ROUTES.DASHBOARD
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);

          if (item.isCenter) {
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center -mt-4"
              >
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg transition-all duration-200',
                    'bg-primary text-primary-foreground',
                    'hover:scale-105 hover:shadow-xl active:scale-95',
                    'ring-4 ring-background',
                  )}
                >
                  <item.icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
              </Link>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex min-w-[3.5rem] flex-col items-center gap-0.5 py-1.5 text-[10px] font-medium transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <item.icon
                className={cn(
                  'h-5 w-5 transition-colors',
                  isActive ? 'text-primary' : 'text-muted-foreground',
                )}
                strokeWidth={isActive ? 2.5 : 1.75}
              />
              <span className={cn(isActive && 'font-semibold')}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
